import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import type { MailClient } from '../api/client';
import type { Email } from '../api/types';
import { useT } from '../i18n';
import AttachmentCard from './AttachmentCard';
import AttachmentPreview from './AttachmentPreview';
import { canPreview } from '../mail/preview';
import { useAccounts } from '../auth/store';
import PlainBody from './PlainBody';
import { addressOf, avatarColor, displayName, fullDate, initials, snippet, spoofSuspected } from '../mail/format';

/*
    Message rendering is the one place hostile input reaches the app, so it is
    handled defensively: sanitise, then render inside a sandboxed iframe whose CSP
    permits nothing by default. Remote images stay blocked until asked for — they
    are a read-tracking vector, and Proton's behaviour is the model.
*/

interface Prepared {
    html: string;
    blockedImages: number;
}

/*
    Inline images arrive as `cid:` references into the message's own parts, and
    no browser can fetch a cid: URL. They are resolved to blob: URLs from the
    parts we already hold — a picture embedded in the message is not a tracker,
    it travelled with the mail, so it renders whether or not remote images are
    unblocked.
*/
function resolveInline(html: string, byCid: Map<string, string>): string {
    if (!byCid.size) return html;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    for (const img of Array.from(doc.querySelectorAll('img'))) {
        const src = img.getAttribute('src') ?? '';
        if (!/^cid:/i.test(src)) continue;
        // Some senders write <img src="cid:<id>"> with the angle brackets kept.
        const cid = decodeURIComponent(src.slice(4)).replace(/^<|>$/g, '');
        const url = byCid.get(cid);
        if (url) img.setAttribute('src', url);
        else img.removeAttribute('src');
    }
    return doc.body.innerHTML;
}

function prepare(html: string, allowImages: boolean): Prepared {
    const clean = DOMPurify.sanitize(html, {
        // Mail from Google (and most senders that use a template) keeps every rule in
        // <style> blocks inside <head>. Sanitising as a fragment throws that away and
        // leaves a wall of unstyled nested tables, so the whole document is kept.
        WHOLE_DOCUMENT: true,
        /*
            `form` etiketi listede ama YETMİYOR: DOMPurify bir etiketi kaldırırken ÇOCUKLARINI
            bırakır. Ölçüldü (2026-09-04, ekrana bakarak — hiçbir test söylemedi): fixture'daki
            `<form><input type="password"><button>` iletisinde form gitmiş, parola kutusu ve
            düğme öksüz olarak GÖVDEDE ÇİZİLİYORDU.

            Gönderemiyorlardı — form yok, betik yok, çerçevenin CSP'si zaten hiçbir yere izin
            vermiyor. Ama bir postanın içinde parola kutusu görmek, kişiye tam olarak oltacının
            istediği şeyi yaptırır: parolasını yazmak. Yazdıktan sonra hiçbir şey olmaması,
            yazmış olmasını geri almıyor.

            Bu yüzden denetimlerin kendileri de yasaklı. Ek olarak `FORBID_CONTENTS` ile formun
            içindekiler tümden atılıyor: yasak etiketin içinde kalan metin de gitsin.
        */
        FORBID_TAGS: [
            'script', 'iframe', 'object', 'embed', 'form', 'link', 'meta', 'base',
            'input', 'button', 'textarea', 'select', 'option', 'optgroup', 'fieldset', 'legend',
        ],
        FORBID_CONTENTS: ['form'],
        FORBID_ATTR: ['srcset', 'ping', 'formaction'],
        ALLOW_DATA_ATTR: false,
    });

    const doc = new DOMParser().parseFromString(clean, 'text/html');
    let blocked = 0;

    if (!allowImages) {
        for (const img of Array.from(doc.querySelectorAll('img'))) {
            const src = img.getAttribute('src') ?? '';
            if (/^https?:/i.test(src)) {
                img.removeAttribute('src');
                img.setAttribute('alt', img.getAttribute('alt') || '');
                blocked++;
            }
        }
        // Tracking pixels also hide in inline background images.
        for (const el of Array.from(doc.querySelectorAll('[style]'))) {
            const style = el.getAttribute('style') ?? '';
            if (/url\(\s*['"]?https?:/i.test(style)) {
                el.setAttribute('style', style.replace(/url\(\s*['"]?https?:[^)]*\)/gi, 'none'));
                blocked++;
            }
        }
    }

    for (const a of Array.from(doc.querySelectorAll('a'))) {
        a.setAttribute('rel', 'noopener noreferrer nofollow');
        a.setAttribute('target', '_blank');
    }

    const headStyles = Array.from(doc.head.querySelectorAll('style'))
        .map((s) => s.outerHTML)
        .join('');

    return { html: headStyles + doc.body.innerHTML, blockedImages: blocked };
}

/*
    HTML mail always renders on white with dark text, whatever theme the app is in.

    Senders author for a white background essentially universally: Google sets its
    own dark greys for text but leaves the page background alone, so painting the
    frame dark gives dark-on-dark and an unreadable message. Every serious client
    — Thunderbird, Proton, Apple Mail — renders HTML mail light for this reason.
    Plain-text bodies are ours to style and do follow the theme; they're rendered
    outside this frame.
*/
function frameDoc(body: string, allowImages: boolean): string {
    // blob: is always allowed — those are this message's own inline parts,
    // already fetched and handed to the frame by us.
    const img = allowImages ? 'https: data: blob:' : 'data: blob:';
    const csp = `default-src 'none'; img-src ${img}; style-src 'unsafe-inline'; font-src data:; media-src 'none'; frame-src 'none'`;
    return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${csp}">
<style>
  html,body{margin:0;padding:0;background:#ffffff;color:#1f1f24;
    font:14px/1.65 'Space Grotesk',system-ui,sans-serif;overflow-wrap:anywhere}
  img{max-width:100%;height:auto}
  table{max-width:100%}
  blockquote{margin:0 0 12px;padding:6px 0 6px 14px;border-left:2px solid rgba(0,0,0,0.18);color:#4a4a55}
  a{color:#5b45c9}
</style></head><body>${body}</body></html>`;
}

/*
    Earlier messages in a thread render as text only. They're context, not the
    message you opened — and it avoids stacking a dozen iframes, each of which
    would need its own height measurement.
*/
function threadText(email: Email): string {
    if (email.text) return email.text;
    if (!email.html) return '';
    return DOMPurify.sanitize(email.html, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

/*
    `email` is the message with its body: undefined while it is being fetched, null when the fetch
    failed. Each case says what it is — this used to be handed the thread's summary row, which has
    no body at all, so every earlier message in every conversation read "(okunabilir içerik yok)"
    and the reader concluded the mail was empty.
*/
function ThreadBody({ email }: { email: Email | null | undefined }) {
    const { t } = useT();
    const content =
        email === undefined ? t('app.loading') : email === null ? t('reader.threadFailed') : threadText(email) || t('reader.noBody');
    return (
        <pre
            dir="auto"
            style={{
                margin: 0,
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-md)',
                lineHeight: 1.6,
                color: 'var(--text-dim)',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                maxHeight: 420,
                overflowY: 'auto',
            }}
        >
            {content}
        </pre>
    );
}

/*
    What can be done with a message from here: save it, unsubscribe from it, mark it as junk.

    Blocking a sender, filing a rule and adding a contact were all in this menu and all removed.
    Each needed something their mail server does not have — blocking and rules are Sieve, which is
    not installed, and contacts were a JMAP address book, which IMAP has no equivalent of. Marking
    junk stays, because that is a message moving to a folder, which the server does understand.

    They are absent rather than disabled. A menu item that explains why it cannot work is still a
    menu item somebody reaches for a second time.
*/
function SenderMenu({
    client,
    email,
    inJunk,
    onSpam,
    onNotSpam,
}: {
    client: MailClient | undefined;
    address: string;
    name: string;
    email: Email;
    inJunk: boolean;
    onSpam?: () => void;
    onNotSpam?: () => void;
}) {
    const subject = email.subject;
    const { t } = useT();
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState<string | null>(null);

    /*
        The .eml is the message exactly as it arrived, headers and all — what you hand to somebody
        debugging a delivery problem, and what an archive keeps.
    */
    const download = () => {
        if (!client) return;
        setOpen(false);
        const name = `${(subject ?? 'message').replace(/[\/:*?"<>|]/g, '_').slice(0, 80)}.eml`;
        // A link rather than a fetch: the browser streams it to disk, so a large message never
        // sits in the page's memory on the way past.
        const link = document.createElement('a');
        link.href = client.rawUrl(email.id, name);
        link.download = name;
        link.click();
    };

    /*
        List-Unsubscribe is opened, never posted automatically.

        RFC 8058 one-click would let us POST on the reader's behalf, and some clients do.
        Unsubscribing is an outward-facing action with a real sender on the other end, so it
        happens because somebody chose it and watched it happen, not because a menu item was
        hovered.
    */
    const unsubscribe = (() => {
        const header = email.listUnsubscribe ?? '';
        const https = /<(https?:[^>]+)>/i.exec(header)?.[1];
        const mailto = /<(mailto:[^>]+)>/i.exec(header)?.[1];
        return https ?? mailto ?? null;
    })();

    const item: React.CSSProperties = {
        display: 'block',
        width: '100%',
        padding: '7px 11px',
        textAlign: 'left',
        background: 'transparent',
        border: 'none',
        color: 'var(--text)',
        fontSize: 'var(--fs-sm)',
        whiteSpace: 'nowrap',
    };

    return (
        <div style={{ marginLeft: 'auto', position: 'relative', flex: '0 0 auto' }}>
            <button
                data-hit
                onClick={() => setOpen((v) => !v)}
                title={t('reader.more')}
                aria-label={t('reader.more')}
                style={{
                    width: 26,
                    height: 26,
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-btn)',
                    color: 'var(--text-dim)',
                    fontSize: 'var(--fs-md)',
                    lineHeight: 1,
                }}
            >
                ⋯
            </button>
            {open ? (
                <>
                    <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                    <div
                        style={{
                            position: 'absolute',
                            right: 0,
                            top: 30,
                            zIndex: 41,
                            minWidth: 210,
                            padding: '4px 0',
                            background: 'var(--surface)',
                            border: '1px solid var(--border2)',
                            borderRadius: 'var(--r-card)',
                            boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
                        }}
                    >
                        {inJunk ? (
                            onNotSpam ? <button style={item} onClick={() => { setOpen(false); onNotSpam(); }}>{t('reader.notSpam')}</button> : null
                        ) : onSpam ? (
                            <button style={item} onClick={() => { setOpen(false); onSpam(); }}>{t('reader.spam')}</button>
                        ) : null}
                        <span style={{ display: 'block', height: 1, margin: '4px 0', background: 'var(--border)' }} />
                        <button style={item} onClick={() => { setOpen(false); window.print(); }}>{t('reader.print')}</button>
                        <button style={item} onClick={() => void download()}>{t('reader.download')}</button>
                        {unsubscribe ? (
                            <button
                                style={item}
                                onClick={() => {
                                    setOpen(false);
                                    window.open(unsubscribe, '_blank', 'noopener,noreferrer');
                                }}
                            >
                                {t('reader.unsubscribe')}
                            </button>
                        ) : null}
                    </div>
                </>
            ) : null}
            {note ? (
                <div
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 32,
                        zIndex: 39,
                        maxWidth: 280,
                        padding: '7px 10px',
                        background: 'var(--surface2)',
                        border: '1px solid var(--border2)',
                        borderRadius: 'var(--r-btn)',
                        fontSize: 'var(--fs-sm)',
                        lineHeight: 1.45,
                        textAlign: 'left',
                    }}
                    onClick={() => setNote(null)}
                >
                    {note}
                </div>
            ) : null}
        </div>
    );
}

export default function Reader({
    client,
    accountId,
    emailId,
    threads,
    junkId,
    sentId,
    onLoaded,
    onSpam,
    onNotSpam,
    onOpen,
}: {
    client: MailClient | undefined;
    accountId: string | undefined;
    emailId: string | null;
    threads?: boolean;
    /** So the menu can offer "not spam" instead of "spam" inside Junk. */
    junkId?: string;
    /** Gönderilmiş klasörü: başlık göndereni değil ALICIYI öne alır. */
    sentId?: string;
    onLoaded?: (email: Email | null) => void;
    onSpam?: () => void;
    onNotSpam?: () => void;
    /** Konuşmadaki önceki bir iletiyi kendi başına, okuyucuda açar. */
    onOpen?: (email: Email) => void;
}) {
    const { t, lang, num } = useT();
    const dateLocale = lang === 'tr' ? 'tr-TR' : 'en-GB';
    const [email, setEmail] = useState<Email | null>(null);
    const [siblings, setSiblings] = useState<Email[]>([]);
    /*
        The conversation arrives as summaries — who, when, which folder — and nothing of what
        each message says. The bodies are fetched here, once the history is opened: a closed
        history costs nothing, and an open one shows a line of every message without having to
        expand each. Fetching a body does not mark it read (the server peeks).
    */
    const [threadBodies, setThreadBodies] = useState<Map<string, Email | null>>(new Map());
    const requestedRef = useRef<Set<string>>(new Set());
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [allowImages, setAllowImages] = useState(false);
    // Konuşma geçmişi kapalı başlar: bu ekranda okunan ileti BU ileti.
    const [historyOpen, setHistoryOpen] = useState(false);
    const [inline, setInline] = useState<Map<string, string>>(new Map());
    const [previewAt, setPreviewAt] = useState<number | null>(null);
    const frameRef = useRef<HTMLIFrameElement>(null);
    const observerRef = useRef<ResizeObserver | null>(null);
    const [frameHeight, setFrameHeight] = useState(320);
    /*
        Whether an office document can be shown at all is a fact about this deployment, not about
        the message: it depends on a converter running beside the server.

        Read up here with the other hooks, and not down beside the attachment list where it is
        used, because below this point the component returns early — while the message is loading
        and when it failed. A hook after those returns runs on some renders and not others, which
        is what React counts, and getting it wrong does not break the reader: it unmounts the
        whole page.
    */
    const canPreviewDocuments = useAccounts((s) => s.canPreviewDocuments);

    useEffect(() => () => observerRef.current?.disconnect(), []);

    // Kept in a ref so a new callback identity doesn't refetch the message.
    const onLoadedRef = useRef(onLoaded);
    onLoadedRef.current = onLoaded;

    useEffect(() => {
        setEmail(null);
        setSiblings([]);
        setThreadBodies(new Map());
        requestedRef.current = new Set();
        setExpanded(new Set());
        setError(null);
        setAllowImages(false);
        setInline(new Map());
        setPreviewAt(null);
        setFrameHeight(320);
        if (!client || !accountId || !emailId) return;
        let cancelled = false;
        setLoading(true);
        void (async () => {
            try {
                const full = await client.getEmailFull(accountId, emailId);
                if (cancelled) return;
                setEmail(full);
                onLoadedRef.current?.(full);

                /*
                    The rest of the conversation.

                    Asked for after the message is on screen rather than with it: the reader is
                    useful the moment the message arrives, and a conversation nobody scrolls to
                    should not delay that.

                    This was left unbuilt originally for a good reason — IMAP THREAD covers one
                    mailbox, so a conversation assembled from the inbox alone is missing every
                    reply the reader wrote, which sit in Sent. The server now merges those in, so
                    the objection no longer holds. Measured: a reply sent from here appears in the
                    thread of the message it answered.

                    A failure here is quietly ignored. The message itself is already readable and
                    an error banner over a missing extra would be noise.
                */
                if (threads) {
                    try {
                        const rest = await client.getThread(emailId);
                        if (!cancelled) setSiblings(rest);
                    } catch {
                        /* conversation unavailable; the message stands on its own */
                    }
                }
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : String(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [client, accountId, emailId, threads]);

    /* Fetches the message's own inline parts so cid: references can resolve. */
    useEffect(() => {
        // From email.inline, not email.attachments: the server files a part as inline only
        // when the HTML actually references its cid, and it never puts those in attachments.
        // Reading the wrong list left this map permanently empty, so no cid: image in any
        // message ever resolved.
        const parts = (email?.inline ?? []).filter((a) => a.cid && a.partId && a.type.startsWith('image/'));
        if (!client || !accountId || !parts.length) return;
        let cancelled = false;
        const urls: string[] = [];
        void (async () => {
            const found = new Map<string, string>();
            for (const part of parts) {
                try {
                    // email.id, not emailId: the parts being downloaded came off THIS message,
                    // and taking the id from anywhere else invites downloading one message's
                    // parts under another's identity. It also retires a non-null assertion that
                    // was standing in for that reasoning.
                    const blob = await client.downloadAttachment(email!.id, part.partId);
                    const url = URL.createObjectURL(blob);
                    urls.push(url);
                    found.set(part.cid!, url);
                } catch {
                    /* one unreachable part shouldn't cost the rest of the message */
                }
            }
            if (cancelled) urls.forEach((u) => URL.revokeObjectURL(u));
            else setInline(found);
        })();
        return () => {
            cancelled = true;
            urls.forEach((u) => URL.revokeObjectURL(u));
        };
    }, [client, accountId, email]);

    useEffect(() => {
        if (!historyOpen || !client || !accountId) return;
        const wanted = siblings.filter((m) => !requestedRef.current.has(m.id));
        if (!wanted.length) return;
        let cancelled = false;
        void (async () => {
            // One at a time, oldest first: the session has two IMAP connections and the message
            // on screen should not queue behind a long conversation.
            for (const m of wanted) {
                if (cancelled) return;
                let full: Email | null;
                try {
                    full = await client.getEmailFull(accountId, m.id);
                } catch {
                    full = null;
                }
                if (cancelled) return;
                // Marked only once the answer is kept. Marking before the await left a message
                // that was in flight when this effect re-ran counted as asked for and never
                // asked for again — "loading" for good.
                requestedRef.current.add(m.id);
                setThreadBodies((prev) => new Map(prev).set(m.id, full));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [historyOpen, client, accountId, siblings]);

    const body = useMemo(() => {
        if (!email) return null;
        /*
            The backend has already chosen which part is the message and decoded it, including
            working out its charset — the interface gets a string, not a tree of parts.

            That decision moved deliberately. Choosing the body is where a mail client gets
            plain text rendered as HTML (every newline collapsing into one paragraph) or an
            attached .txt displayed instead of the actual message, and it is the same decision
            for every client. It belongs next to the mail, not in the browser.
        */
        const htmlPart = email.html?.trim() ? { partId: 'html' } : null;
        const textPart = email.text?.trim() ? { partId: 'text' } : null;

        if (htmlPart) {
            const raw = email.html ?? '';
            const clean = prepare(raw, allowImages);
            return { kind: 'html' as const, ...clean, html: resolveInline(clean.html, inline) };
        }
        if (textPart) {
            return { kind: 'text' as const, text: email.text ?? '', blockedImages: 0 };
        }
        return { kind: 'none' as const, blockedImages: 0 };
    }, [email, allowImages, inline]);

    if (!emailId) {
        return (
            <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--text-mute)', background: 'var(--surface)' }}>
                <div style={{ textAlign: 'center' }}>
                    <div className="kicker">// {t('reader.nothing')}</div>
                    <div style={{ marginTop: 6, fontSize: 'var(--fs-md)' }}>{t('reader.pick')}</div>
                </div>
            </div>
        );
    }

    if (error) {
        return <div style={{ flex: 1, padding: 20, color: 'var(--red)', fontSize: 'var(--fs-md)', background: 'var(--surface)' }}>{error}</div>;
    }

    if (loading || !email) {
        return (
            <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: 'var(--text-mute)', background: 'var(--surface)' }}>
                <span className="kicker">// {t('app.loading')}</span>
            </div>
        );
    }

    /*
        `name`/`addr` GÖNDERENDİR ve öyle kalmalı: SenderMenu spam/engelleme sunuyor, o menü
        iletiyi gerçekten kimin yolladığına bakmak zorunda. Başlıkta GÖRÜNEN karşı taraf ayrı
        hesaplanıyor.
    */
    const name = displayName(email.from);
    const addr = addressOf(email.from);
    /*
        Gönderilmiş bir iletide gönderen okuyanın kendisidir; başlığın kalın ilk satırı orada
        kişinin kendi adını yazarsa hiçbir şey söylemez. Listede aynı düzeltme yapıldı
        (MessageList), okuma bölmesinde eksik kalmıştı.

        Gönderen KAYBOLMUYOR — soluk satıra iniyor. Birden çok alıcı varsa tam liste de ayrı bir
        soluk satırda duruyor, yani vurgu değişiyor, bilgi değil.
    */
    const gonderilmis = Boolean(sentId && email.folder === sentId && email.to?.length);
    const basAd = gonderilmis ? displayName(email.to) : name;
    const basAdres = gonderilmis ? addressOf(email.to) : addr;
    const cokAlici = (email.to?.length ?? 0) > 1;
    const spoof = spoofSuspected(email.authResults);
    // A part shown inside the message is not also a file to download.
    const attachments = (email.attachments ?? []).filter(
        (a) => !(a.cid && inline.has(a.cid)) && (a.disposition !== 'inline' || a.name),
    );
    const previewable = attachments.filter((a) => canPreview(a, canPreviewDocuments));

    return (
        <div data-print-root style={{ flex: 1, overflowY: 'auto', background: 'var(--surface)' }}>
            <div style={{ maxWidth: 860, margin: '0 auto', padding: '22px 26px 60px' }}>
                {/*
                    Konu BAŞA geçti (arayüz v2, 3. adım). Sebep sıralamanın kendisi: bir iletiyi
                    açan kişi önce "bu ne hakkında" diye bakar, "kimden" ikinci sorudur. Konu
                    gönderen bloğunun altındayken gözün ilk düştüğü yer bir avatardı.
                */}
                <h1
                    dir="auto"
                    style={{
                        margin: '0 0 14px',
                        fontSize: 'var(--fs-2xl)',
                        fontWeight: 600,
                        letterSpacing: '-0.5px',
                        lineHeight: 1.3,
                    }}
                >
                    {email.subject || t('list.noSubject')}
                </h1>

                <div style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
                    <div
                        style={{
                            width: 40,
                            height: 40,
                            flex: '0 0 auto',
                            borderRadius: 'var(--r-pill)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 'var(--fs-lg)',
                            fontWeight: 700,
                            color: '#fff',
                            background: avatarColor(basAdres || basAd),
                        }}
                    >
                        {initials(basAd)}
                    </div>
                    <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                        <div data-testid="reader-person" style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {basAd}
                        </div>
                        {/* Mono değil: bir e-posta adresi hizalanacak bir sayı değil, okunacak
                            bir ad. Mono artık yalnız sayı, tarih, boyut, sunucu yolu ve kısayol
                            için kalıyor. */}
                        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', overflowWrap: 'anywhere' }}>
                            {basAdres}
                        </div>
                        {/* "alıcı: …" konunun altından buraya taşındı: kime yazıldığı gönderenin
                            kim olduğuyla aynı soruya ait, iletinin konusuyla değil. */}
                        {gonderilmis && cokAlici ? (
                            <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-faint)', marginTop: 2, overflowWrap: 'anywhere' }}>
                                {t('reader.to', { names: email.to?.map((a) => a.name || a.email).join(', ') || '—' })}
                            </div>
                        ) : null}
                        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-faint)', marginTop: 2, overflowWrap: 'anywhere' }}>
                            {gonderilmis
                                ? t('reader.from', {
                                      /*
                                          Adı olmayan bir adreste `displayName` adresin kendisini
                                          döndürür; `ad <adres>` biçimi o zaman aynı şeyi iki kez
                                          yazar ("x@y <x@y>"). Canlıda görüldü, kozmetik değil:
                                          okuyan kişi iki farklı şey sanıp bakıyor.
                                      */
                                      names: name && addr && name !== addr ? `${name} <${addr}>` : addr || name || '—',
                                  })
                                : t('reader.to', { names: email.to?.map((a) => a.name || a.email).join(', ') || '—' })}
                        </div>
                    </div>
                    <SenderMenu
                        client={client}
                        address={addr}
                        name={name}
                        email={email}
                        inJunk={Boolean(junkId && email.folder === junkId)}
                        onSpam={onSpam}
                        onNotSpam={onNotSpam}
                    />
                    <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
                            {fullDate(email.receivedAt, dateLocale)}
                        </div>
                    </div>
                </div>


                {/*
                    Yalnız bir şey ters gittiğinde, düz dille. Doğrulama geçtiğinde ekranda hiçbir şey
                    yok: neredeyse her gerçek ileti geçer, ve "doğrulandı" rozeti "güvenli" diye
                    okunuyordu — oysa benzer bir alan adı alan dolandırıcı da geçer. Teknik ayrıntı
                    isteyene `title` içinde. Kural `spoofSuspected`de.
                */}
                {spoof.suspect ? (
                    <div
                        role="alert"
                        data-testid="spoof-warning"
                        title={spoof.detail}
                        style={{
                            margin: '12px 0 0',
                            padding: '10px 12px',
                            background: 'var(--red-bg)',
                            borderLeft: '3px solid var(--red)',
                            borderRadius: 'var(--r-input)',
                            color: 'var(--text)',
                            fontSize: 'var(--fs-sm)',
                            lineHeight: 1.5,
                        }}
                    >
                        <strong style={{ fontWeight: 600 }}>{t('reader.spoofTitle')}</strong> {t('reader.spoofBody')}
                    </div>
                ) : null}

                {/*
                    Konuşma geçmişi artık AÇIK bir kart yığını değil, tek satır.

                    Sebep ölçülebilir bir şey: bu ekranda okunan ileti BU ileti. Önceki üç ileti,
                    her biri kendi kartıyla, okunan iletiyi ekranın aşağısına itiyordu — yani
                    ekranın en değerli yeri, kişinin zaten okumuş olduğu şeye gidiyordu. Geçmiş
                    duruyor, bir tıklama uzağa taşındı.

                    Kesikli çerçeve bilerek: bu bir kart değil, açılabilir bir özet.
                */}
                {siblings.length ? (
                    <div style={{ display: 'grid', gap: 6, marginBottom: 16 }}>
                        <button
                            data-testid="thread-history"
                            data-hit
                            aria-expanded={historyOpen}
                            onClick={() => setHistoryOpen((v) => !v)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                width: '100%',
                                padding: '8px 12px',
                                background: 'transparent',
                                border: '1px dashed var(--border2)',
                                borderRadius: 'var(--r-card)',
                                textAlign: 'left',
                                color: 'var(--text-dim)',
                            }}
                        >
                            <span style={{ display: 'flex', flex: '0 0 auto' }}>
                                {siblings.slice(0, 2).map((m, i) => {
                                    const who = displayName(m.from);
                                    return (
                                        <span
                                            key={m.id}
                                            aria-hidden="true"
                                            style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: 'var(--r-pill)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 9,
                                                fontWeight: 700,
                                                color: '#fff',
                                                background: avatarColor(addressOf(m.from) || who),
                                                border: '2px solid var(--surface)',
                                                marginLeft: i ? -7 : 0,
                                            }}
                                        >
                                            {initials(who)}
                                        </span>
                                    );
                                })}
                            </span>
                            <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--fs-sm)' }}>
                                {siblings.length === 1
                                    ? t('reader.historyOne')
                                    : t('reader.history', { count: num(siblings.length) })}
                            </span>
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                style={{ flex: '0 0 auto', transform: historyOpen ? 'rotate(180deg)' : 'none' }}
                            >
                                <path d="M7 10l5 5 5-5" />
                            </svg>
                        </button>
                        {historyOpen ? siblings.map((m) => {
                            const open = expanded.has(m.id);
                            const who = displayName(m.from);
                            const full = threadBodies.get(m.id);
                            return (
                                <div key={m.id} data-testid="thread-body" style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-card)', background: 'var(--surface2)', overflow: 'hidden' }}>
                                    <button
                                        onClick={() =>
                                            setExpanded((prev) => {
                                                const next = new Set(prev);
                                                if (next.has(m.id)) next.delete(m.id);
                                                else next.add(m.id);
                                                return next;
                                            })
                                        }
                                        data-hit
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', background: 'transparent', border: 'none', textAlign: 'left' }}
                                    >
                                        <span style={{ width: 24, height: 24, flex: '0 0 auto', borderRadius: 'var(--r-pill)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-2xs)', fontWeight: 700, color: '#fff', background: avatarColor(addressOf(m.from) || who) }}>
                                            {initials(who)}
                                        </span>
                                        <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, flex: '0 1 auto', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{who}</span>
                                        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--fs-sm)', color: 'var(--text-mute)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {open || !full ? '' : snippet(threadText(full))}
                                        </span>
                                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'var(--text-mute)', flex: '0 0 auto' }}>{fullDate(m.receivedAt, dateLocale)}</span>
                                    </button>
                                    {open ? (
                                        <div style={{ padding: '0 12px 12px' }}>
                                            <ThreadBody email={threadBodies.has(m.id) ? full : undefined} />
                                            {/*
                                                Burada gösterilen düz metin bir göz atma; ek, biçim ve yanıtla
                                                yok. İletinin kendisi okuyucuda açılır — pencere içinde pencere
                                                değil, geri tuşu da buraya döndürür.
                                            */}
                                            {onOpen ? (
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                                                    <button
                                                        data-testid="thread-open"
                                                        data-hit
                                                        onClick={() => onOpen(m)}
                                                        style={{
                                                            padding: '6px 12px',
                                                            background: 'var(--accent-bg)',
                                                            color: 'var(--accent-strong)',
                                                            border: 'none',
                                                            borderRadius: 'var(--r-btn)',
                                                            fontSize: 'var(--fs-sm)',
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {t('reader.openMessage')}
                                                    </button>
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        }) : null}
                    </div>
                ) : null}

                {body?.blockedImages ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 10,
                            padding: '10px 12px',
                            marginBottom: 14,
                            background: 'var(--amber-bg)',
                            border: '1px solid var(--amber)',
                            borderRadius: 'var(--r-btn)',
                        }}
                    >
                        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                            <div style={{ fontSize: 'var(--fs-sm)' }}>
                                {t(body.blockedImages === 1 ? 'reader.blockedOne' : 'reader.blocked', { count: num(body.blockedImages) })}
                            </div>
                            {/* --text-dim: sinifin kendi --text-mute'u --amber-bg uzerinde koyu
                                temada 4.31:1 veriyor. Bu serit bugune kadar hic olculmemisti —
                                yerelde HTML ileti yoktu, yani hic cizilmiyordu. */}
                            <div className="kicker" style={{ marginTop: 2, color: 'var(--text-dim)' }}>// {t('reader.blockedWhy')}</div>
                        </div>
                        <button
                            data-testid="load-images"
                            data-hit
                            onClick={() => setAllowImages(true)}
                            style={{
                                height: 26,
                                padding: '0 11px',
                                /*
                                    Teslimin kendi uyarısı: beyaz/koyu yazı kehribar dolgu üstünde
                                    3.64:1 veriyor ve `--amber` açık temada koyu bir kehribar
                                    (#b7791f) — sabit `#1a1206` orada koyu-üstüne-koyu oluyordu.
                                    Dolgu --amber-bg, yazı --text, çerçeve --amber.
                                */
                                background: 'var(--amber-bg)',
                                color: 'var(--text)',
                                border: '1px solid var(--amber)',
                                borderRadius: 'var(--r-btn)',
                                fontSize: 'var(--fs-sm)',
                                fontWeight: 600,
                            }}
                        >
                            {t('reader.loadImages')}
                        </button>
                    </div>
                ) : null}

                {body?.kind === 'html' ? (
                    <div
                        style={{
                            background: '#ffffff',
                            border: '1px solid var(--border2)',
                            borderRadius: 'var(--r-card)',
                            overflow: 'hidden',
                            padding: 14,
                        }}
                    >
                    <iframe
                        ref={frameRef}
                        title="message"
                        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                        srcDoc={frameDoc(body.html, allowImages)}
                        onLoad={() => {
                            const doc = frameRef.current?.contentDocument;
                            if (!doc) return;
                            const measure = () => {
                                // Template mail often sets html{height:100%}, which makes
                                // documentElement.scrollHeight useless on its own.
                                const h = Math.max(doc.body?.scrollHeight ?? 0, doc.documentElement.scrollHeight);
                                setFrameHeight(Math.max(120, h + 16));
                            };
                            measure();
                            // Media queries and late layout settle after load; keep following it.
                            if (doc.body && typeof ResizeObserver !== 'undefined') {
                                observerRef.current?.disconnect();
                                const ro = new ResizeObserver(measure);
                                ro.observe(doc.body);
                                observerRef.current = ro;
                            }
                        }}
                        style={{ width: '100%', height: frameHeight, border: 'none', background: '#ffffff', display: 'block' }}
                    />
                    </div>
                ) : body?.kind === 'text' ? (
                    <PlainBody text={body.text} />
                ) : (
                    <div style={{ color: 'var(--text-mute)', fontSize: 'var(--fs-md)' }}>{t('reader.noBody')}</div>
                )}

                {attachments.length ? (
                    <div style={{ marginTop: 22, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                        <div className="kicker" style={{ marginBottom: 9 }}>
                            // {t(attachments.length === 1 ? 'reader.attachmentsOne' : 'reader.attachments', { count: num(attachments.length) })}
                        </div>
                        {/* A grid, not a row of chips: cards are wide enough to show a picture
                            and a whole file name, and they wrap onto a phone by themselves. */}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))',
                                gap: 'var(--sp-4)',
                            }}
                        >
                            {attachments.map((a) => (
                                <AttachmentCard
                                    key={a.partId ?? a.name}
                                    client={client}
                                    emailId={email.id}
                                    part={a}
                                    openable={canPreview(a, canPreviewDocuments)}
                                    onOpen={async () => {
                                        if (!client || !accountId || !a.partId) return;
                                        // Openable things open; everything else saves.
                                        const at = previewable.findIndex((p) => p.partId === a.partId);
                                        if (at !== -1) {
                                            setPreviewAt(at);
                                            return;
                                        }
                                        // Straight to the server: the browser streams it to disk
                                        // and a large attachment never sits in the page's memory.
                                        const link = document.createElement('a');
                                        link.href = client.attachmentUrl(email.id, a.partId);
                                        link.rel = 'noopener';
                                        link.click();
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                ) : null}
            </div>

            {previewAt !== null && previewable.length ? (
                <AttachmentPreview
                    client={client}
                    emailId={email.id}
                    attachments={previewable}
                    index={Math.min(previewAt, previewable.length - 1)}
                    onIndex={setPreviewAt}
                    onClose={() => setPreviewAt(null)}
                />
            ) : null}
        </div>
    );
}
